/**
 * Created by bcole on 1/12/20.
 */
import React, {createContext, useReducer} from "react";
import Reducer from "./Reducer"
import {DEFAULT_NEW_TASK_DRAFT} from "./newTaskDraft";

const initialState = {
    user: {
        id: null,
        name: null,
        accessToken: null,
    },
    projects: [

    ],
    project_id: null,
    project_code: null,
    project_name: null,
    sprints: [],
    // The current user's effective permission keys on this project (see
    // api/lib/permissions.js) - fetched once per project load (Project.js)
    // and used to hide nav/tabs the user can't act on (Sidebar.js,
    // ProjectSettings.js). Empty until that fetch resolves.
    permissions: [],
    current_sprint: -1,
    // The sprint Kanban should open to - only ever meaningful for that one
    // route (every other page dropped its sprint segment from its own URL;
    // see Project.js's effectiveSprintId/effectiveSprintIndex). Kept here
    // rather than only in the URL so choosing a sprint from a sprint-less
    // page still "sticks" the next time Kanban is opened. Seeded from
    // current_sprint until the user actually picks one of their own.
    selected_sprint_id: null,
    current_project: -1,
    current_phase: -1,
    selected_phase: -1,
    swimlanes: [],
    /*
     * The tickets the Backlog page has loaded, in two parts fetched separately
     * and kept in one array (the page derives its columns from `sprintId`):
     *
     *  - the Backlog column - tickets with no sprint - a page at a time, as far
     *    as the page has scrolled (SET_BACKLOG_TICKETS / APPEND_BACKLOG_TICKETS,
     *    from GET /projects/:id/backlog),
     *  - the visible sprints' tickets, all of them (SET_SPRINT_TICKETS, from
     *    GET /projects/:id/tickets?currentSprintIds=...).
     *
     * Not "every ticket in the project", which is what this used to be. The
     * sprint half has a natural ceiling - a sprint holds what a team can do in a
     * fortnight - but the backlog holds everything anybody has ever thought of,
     * and loading all of it to render fifty cards is what the paging replaced.
     *
     * In the store rather than in SprintBacklog.js so a live "entity:patched"
     * broadcast has somewhere to land (see the realtime listener in Project.js) -
     * the same reason `bugs` and `supportTickets` below are here.
     */
    tickets: [],
    // Where the Backlog column has got to. `cursor` is opaque - the server's
    // `nextCursor`, handed back unchanged to ask for the next page. `total` is
    // how many tickets matched the current filters when the first page was
    // fetched, so the column can say "50 of 312"; null until it answers, and
    // only adjusted afterwards for changes this session can actually see (see
    // UPDATE_TASK in Reducer.js).
    backlogPaging: {cursor: null, hasMore: false, total: null},
    ticketTemplate: [

    ],
    bugTemplate: [

    ],
    // A project's bugs, newest first, as far as ReviewBugs.js has scrolled -
    // filtered by the server, not by bugFilters below. Those filters are still
    // applied client-side on top of this, but only to catch a live patch that
    // has taken a bug out of the filtered set: the paging itself has to run
    // over the filtered list server-side or everything past the first page
    // would be missed (see api/lib/listFilters.js).
    bugs: [],
    // Where the bug list has got to - same shape and meaning as backlogPaging.
    bugsPaging: {cursor: null, hasMore: false, total: null},
    // A project's support queue, newest first, as far as SupportQueue.js has
    // scrolled - the Support page reads this the same way ReviewBugs.js reads
    // `bugs`, and it's filtered and paged by the server for the same reasons.
    supportTickets: [],
    // Where the queue has got to - same shape and meaning as backlogPaging.
    supportTicketsPaging: {cursor: null, hasMore: false, total: null},
    // The distinct support levels the API reported alongside the first page of
    // the queue - the options for its level filter. Comes from the server
    // rather than from the loaded rows: the queue pages, so the levels present
    // in `supportTickets` are only the ones on the pages fetched so far.
    supportTicketLevels: [],
    // What the current project's plan allows - see GET /projects/:id/plan-limits.
    // Null until fetched, which the screens read as "don't block anything yet"
    // rather than flashing an upgrade prompt at somebody who has the feature.
    planLimits: null,
    backlog: {
        name: "",
        tasks: []
    },
    scope: {
        phase: [],
        scope_types: []
    },
    scope_items: {},
    // The Backlog page's own filter bar (SprintBacklog.js) - kept in the
    // store rather than that component's local state so the selections
    // survive navigating away and back to the page.
    backlogFilters: {
        phase: "",
        scope: "",
        assignee: "",
        type: "",
        priority: "",
        text: "",
    },
    // The Bugs page's own filter bar (Bugs/ReviewBugs.js) - kept in the
    // store for the same reason backlogFilters is: selections survive
    // navigating away and back to the page.
    bugFilters: {
        type: "",
        status: "",
        open: "",
    },
    // The Support queue's filter bar (Support/SupportQueue.js), in the store
    // for the same reason bugFilters is. `owner` holds a member id as a string,
    // or the literals "me" / "unassigned"; `needsAction` is "" or "yes";
    // `level` is "none" or a "level:<folded>" value built by supportFilters.js,
    // whose options come from the tickets rather than from a fixed list.
    supportFilters: {
        status: "",
        open: "",
        owner: "",
        needsAction: "",
        level: "",
    },
    // Every design page this session has loaded, keyed by "<typeSlug>/<scopeSlug>"
    // (see Design/DesignPage.js) - not fetched up front (there's no listing
    // of them all), just filled in as pages are visited. A live patch from
    // another user (see the realtime "entity:patched" listener in
    // Project.js) only ever needs to reach a page already keyed here - one
    // nobody's viewing gets a fresh copy on next visit regardless.
    designPages: {},
    // The New Task modal's in-progress fields (components/Project/NewTicket.js),
    // kept here instead of in that component's own state so a click on the
    // modal's dimmed backdrop - which only hides the dialog, not a deliberate
    // "never mind" - leaves the draft to come back next time the modal opens.
    // Reset to DEFAULT_NEW_TASK_DRAFT by the X button and by a successful
    // Save, both of which do mean "done with this".
    newTaskDraft: DEFAULT_NEW_TASK_DRAFT,
};

export const Context = createContext(initialState);

const Store = ({children}) => {
    const { Provider } = Context;

    const [state, setState] = useReducer(Reducer, initialState);
    return (
        <Provider value={ [ state, setState ] }>
            {children}
        </Provider>
    )
};

export default Store;